import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { ParameterService } from 'services/ParametersProviderApi/ParametersService';
import { injectJwtTokenIntoHeaders } from '../tokenLocalStorage';
import { stringify } from 'qs';
import {
  ItineraryVersion,
  ValidateOtpResponse,
  VouchersV2OverviewResponse,
  VoucherView,
  IVoucherV2Upload,
} from './types';
import { ItineraryTypes } from 'containers/Itinerary/types';

export class ItineraryManagerApiService<T extends AxiosWrapper<AxiosInstance>> {
  client: T;

  constructor(client: T) {
    this.client = client;
  }

  getItineraryVersionList = async (bookingUuid: string): Promise<AxiosResponse<ItineraryVersion[]>> => {
    const endpoint = `/viewer/v1/version-list/${bookingUuid}`;
    return this.client.get(endpoint);
  };

  getItineraryView = async (bookingUuid: string, version: string): Promise<AxiosResponse> => {
    const endpoint = `/viewer/v1/view/${bookingUuid}/${version}`;
    return this.client.get(endpoint);
  };

  // this is Step 1 of the upload process
  getUploadUrl = async ({
    bookingUuid,
    contentType,
    fileName,
  }: {
    bookingUuid: string;
    contentType: string;
    fileName: string;
  }): Promise<AxiosResponse> => {
    const endpoint = `viewer/v1/media/upload-url/${bookingUuid}`;
    return this.client.post(endpoint, {
      contentType,
      fileName,
    });
  };

  // this is Step 3 of the upload process
  postProcessUpload = async ({
    bookingUuid,
    displayName,
    filePath,
    fileType,
  }: {
    bookingUuid: string;
    displayName: string;
    filePath: string;
    fileType: 'image' | 'file';
  }): Promise<AxiosResponse> => {
    const endpoint = `viewer/v1/media/process-upload/${bookingUuid}`;
    return this.client.post(endpoint, {
      displayName,
      filePath,
      fileType,
    });
  };

  saveItinerary = async (
    bookingUuid: string,
    version: string,
    data: ItineraryTypes['ItineraryData']
  ): Promise<AxiosResponse> => {
    const endpoint = `/viewer/v1/save/${bookingUuid}/${version}`;
    return this.client.post(endpoint, {
      itinerary: data,
    });
  };

  publishItinerary = async (bookingUuid: string, version: string): Promise<AxiosResponse> => {
    const endpoint = `/viewer/v1/publish/${bookingUuid}/${version}`;
    return this.client.post(endpoint);
  };

  getVouchersV2Overview = async (bookingUuid: string): Promise<AxiosResponse<VouchersV2OverviewResponse>> => {
    const endpoint = `/voucher/v1/overview/${bookingUuid}`;
    return this.client.get(endpoint);
  };
  getVouchersV2View = async (bookingUuid: string, accessUuid: string | null): Promise<AxiosResponse<VoucherView>> => {
    const endpoint = `/voucher/v1/view/${bookingUuid}`;
    const config = accessUuid ? { params: { accessUuid } } : undefined;
    return this.client.get(endpoint, config);
  };

  checkIsOtpRequired = async (itemUuid: string, accessUuid: string | null): Promise<AxiosResponse> => {
    const endpoint = `/general/v1/otp/required/${itemUuid}`;
    const config = accessUuid ? { params: { accessUuid } } : undefined;
    return this.client.get(endpoint, config);
  };

  requestOtp = async (itemUuid: string, email: string): Promise<AxiosResponse> => {
    const endpoint = `/general/v1/otp/new/${itemUuid}`;
    return this.client.post(endpoint, {
      email,
    });
  };

  validateOtp = async (itemUuid: string, email: string, otp: string): Promise<AxiosResponse<ValidateOtpResponse>> => {
    const endpoint = `/general/v1/otp/validate/${itemUuid}`;
    return this.client.post(endpoint, {
      email,
      otp,
    });
  };

  refreshOtp = async (itemUuid: string, accessUuid: string): Promise<AxiosResponse> => {
    const endpoint = `/general/v1/otp/refresh/${itemUuid}`;
    return this.client.post(endpoint, undefined, {
      params: { accessUuid },
    });
  };

  getVoucherV2AccessList = async (voucherUuid: string): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/access/${voucherUuid}`;
    return this.client.get(endpoint);
  };

  addToVoucherV2AccessList = async (
    voucherUuid: string,
    firstName: string,
    lastName: string,
    email: string
  ): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/access/${voucherUuid}/add`;
    return this.client.post(endpoint, {
      firstName,
      lastName,
      email,
    });
  };

  removeFromVoucherV2AccessList = async (voucherUuid: string, email: string): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/access/${voucherUuid}/remove`;
    return this.client.post(endpoint, {
      email,
    });
  };

  saveVoucher = async (voucherUuid: string, data: any): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/save/${voucherUuid}`;
    return this.client.post(endpoint, {
      voucher: data,
    });
  };

  // Voucher upload methods
  getVoucherUploadUrl = async ({
    voucherUuid,
    contentType,
    fileName,
  }: {
    voucherUuid: string;
    contentType: string;
    fileName: string;
  }): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/media/upload-url/${voucherUuid}`;
    return this.client.post(endpoint, {
      contentType,
      fileName,
    });
  };

  postVoucherProcessUpload = async ({
    voucherUuid,
    displayName,
    filePath,
    fileType,
  }: {
    voucherUuid: string;
    displayName: string;
    filePath: string;
    fileType: 'image' | 'file';
  }): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/media/process-upload/${voucherUuid}`;
    return this.client.post(endpoint, {
      displayName,
      filePath,
      fileType,
    });
  };

  generatePdf = async (voucherUuid: string, accessUuid: string | null | undefined): Promise<AxiosResponse> => {
    const endpoint = `/voucher/v1/pdf/${voucherUuid}`;
    const config = accessUuid ? { params: { accessUuid } } : undefined;
    return this.client.post(endpoint, undefined, config);
  };

  getVoucherPdfUploads = async (voucherUuid: string): Promise<AxiosResponse<IVoucherV2Upload[]>> => {
    const endpoint = `/voucher/v1/pdf/${voucherUuid}/list`;
    return this.client.get(endpoint);
  };
}

export const makeItineraryManagerApi = (): ItineraryManagerApiService<AxiosWrapper<AxiosInstance>> => {
  const dynamicParameters = ParameterService.getParameters();
  const headers: any = {};

  const client = axios.create({
    baseURL: dynamicParameters.ITINERARY_MANAGER_BASE_URL,
    headers: injectJwtTokenIntoHeaders(headers),
    withCredentials: true,
    paramsSerializer: stringify,
  });

  const clientWrapper = new AxiosWrapper(client);

  return new ItineraryManagerApiService<AxiosWrapper<AxiosInstance>>(clientWrapper);
};
